import {
  Dispatch,
  Fragment,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Channel,
  OrganisationUserManagementStatus,
  UserChannelAccess,
  UserRole,
} from '../../../../services';
import {
  Check,
  ConfirmationModal,
  getCssVar,
  getWebAppUrl,
  IconLabel,
  IconLabelSizes,
  ListOpenDirection,
  Modal,
  SearchField,
  Select,
  useAppDispatch,
  useAppSelector,
  useDebounce,
} from '../../../../shared';
import {
  organisationUserInviteByUserId,
  organisationUserRemoveFromChannel,
  organisationUserUpdateChannelRole,
} from '../../../../slices';

import classes from './UserChannelsModal.module.scss';

export interface UserChannels {
  userId: number;
  screenName: string;
  accessedChannels: UserChannelAccess[];
  isOrgOwnerOrAdmin: boolean;
  showNotifyUsers: boolean;
}

interface UserChannelRoleArgs {
  channelId: number;
  index: number;
  role?: UserRole;
}

interface ChannelRoleUpdateArgs {
  channelId: number;
  role: UserRole;
}

interface ChannelClickArgs {
  subdomain: string;
  isArchived: boolean;
}

interface UserChannelsModalProps {
  userChannels: UserChannels | null;
  setUserChannels: Dispatch<SetStateAction<UserChannels | null>>;
  callback: () => void;
}

export const UserChannelsModal: FunctionComponent<UserChannelsModalProps> = memo(
  ({ userChannels, setUserChannels, callback }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsers' });

    const dispatch = useAppDispatch();

    const { channels } = useAppSelector(({ channels }) => channels);

    const [channelsToDisplay, setChannelsToDisplay] = useState<Channel[]>([]);

    const [channelIdToRemoveFrom, setChannelIdToRemoveFrom] = useState<number | null>(null);

    const [deleteLoading, setDeleteLoading] = useState(false);

    const [notifyExistingUsers, setNotifyExistingUsers] = useState<boolean>(false);

    const [query, setQuery] = useState<string>('');

    const debouncedQuery = useDebounce<string>(query);

    const {
      userId = 0,
      screenName = '',
      accessedChannels = [],
      isOrgOwnerOrAdmin = false,
      showNotifyUsers = false,
    } = { ...userChannels };

    const lastChannelAccess = useMemo(
      () => accessedChannels.length === 1,
      [accessedChannels.length]
    );

    useEffect(() => {
      setChannelsToDisplay(
        [...channels].filter(({ name }) => name.toLowerCase().startsWith(query.toLowerCase()))
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels, debouncedQuery]);

    const inviteToChannelHandler = useCallback(
      async (channelId: number) => {
        if (!userChannels) {
          return;
        }

        try {
          const { userManagementResult } = await dispatch(
            organisationUserInviteByUserId({
              channelIds: [channelId],
              userIds: [userId],
              role: UserRole.CHANNEL_READER,
              notifyExistingUsers,
            })
          ).unwrap();

          if (userManagementResult[0].status !== OrganisationUserManagementStatus.SKIP) {
            const accessedChannelsCopy = [...userChannels.accessedChannels];

            accessedChannelsCopy.push({
              id: channelId,
              name: '',
              subdomain: '',
              role: UserRole.CHANNEL_READER,
            });

            setUserChannels({ ...userChannels, accessedChannels: accessedChannelsCopy });
            return;
          }

          toast.error(t('common.error-message'));
        } catch (_) {
          toast.error(t('common.error-message'));
        }
      },
      [dispatch, notifyExistingUsers, setUserChannels, t, userChannels, userId]
    );

    const onChannelRoleUpdate = useCallback(
      async ({ channelId, role }: ChannelRoleUpdateArgs) => {
        if (!userChannels) {
          return;
        }

        try {
          const { userManagementResult } = await dispatch(
            organisationUserUpdateChannelRole({ channelIds: [channelId], userIds: [userId], role })
          ).unwrap();

          if (userManagementResult[0].status === OrganisationUserManagementStatus.OK) {
            const accessedChannels = userChannels.accessedChannels.map((channel) =>
              channel.id === channelId ? { ...channel, role } : channel
            );

            setUserChannels({ ...userChannels, accessedChannels });
            return;
          }

          toast.error(t('common.error-message'));
        } catch (_) {
          toast.error(t('common.error-message'));
        }
      },
      [dispatch, setUserChannels, t, userChannels, userId]
    );

    const renderUserChannelRole = useCallback(
      ({ channelId, index, role }: UserChannelRoleArgs) => {
        if (!role) {
          return (
            <div className={classes['channels__item-role']}>
              <IconLabel
                iconId={'add-plus'}
                iconSize={18}
                label={ctxT('add-into-channel')}
                onClick={() => inviteToChannelHandler(channelId)}
                labelFirst
              />
            </div>
          );
        }

        return (
          <Select
            className={classes['channels__item-role']}
            selectedItemId={role}
            items={[
              { id: UserRole.CHANNEL_ADMIN, title: t('userRoles.CHANNEL_ADMIN') },
              { id: UserRole.CHANNEL_EDITOR, title: t('userRoles.CHANNEL_EDITOR') },
              { id: UserRole.CHANNEL_EDITOR_LIMITED, title: t('userRoles.CHANNEL_EDITOR_LIMITED') },
              { id: UserRole.CHANNEL_READER, title: t('userRoles.CHANNEL_READER') },
            ]}
            onChange={(role) => onChannelRoleUpdate({ channelId, role } as ChannelRoleUpdateArgs)}
            listOpenDirection={
              channelsToDisplay.length <= 3 ||
              index + 1 <= Math.round(0.5 * channelsToDisplay.length)
                ? ListOpenDirection.bottom
                : ListOpenDirection.top
            }
          />
        );
      },
      [channelsToDisplay.length, ctxT, inviteToChannelHandler, onChannelRoleUpdate, t]
    );

    const channelClickHandler = useCallback(
      ({ subdomain, isArchived }: ChannelClickArgs) => {
        if (isArchived) {
          toast.info(t('orgDashboardChannels.archived-click-message'));
          return;
        }
        window.open(`${getWebAppUrl(subdomain)}/admin`, '_blank', 'noopener, noreferrer');
      },
      [t]
    );

    const onClose = useCallback(() => {
      setQuery('');
      setUserChannels(null);
      setNotifyExistingUsers(false);
    }, [setUserChannels]);

    const removeFromChannelHandler = useCallback(async () => {
      if (!userChannels || !channelIdToRemoveFrom) {
        return;
      }

      setDeleteLoading(true);

      try {
        const { userManagementResult } = await dispatch(
          organisationUserRemoveFromChannel({
            channelIds: [channelIdToRemoveFrom],
            userIds: [userId],
          })
        ).unwrap();

        if (!userManagementResult.length) {
          toast.error(t('common.error-message'));
          return;
        }

        if (userManagementResult[0].status !== OrganisationUserManagementStatus.OK) {
          toast.info(ctxT('remove-from-channel-skip'));
          return;
        }

        if (lastChannelAccess) {
          onClose();
          callback();
          return;
        }

        setUserChannels({
          ...userChannels,
          accessedChannels: userChannels.accessedChannels.filter(
            ({ id }) => id !== channelIdToRemoveFrom
          ),
        });
      } catch (_) {
        toast.error(t('common.error-message'));
      } finally {
        setDeleteLoading(false);
        setChannelIdToRemoveFrom(null);
      }
    }, [
      callback,
      channelIdToRemoveFrom,
      ctxT,
      dispatch,
      lastChannelAccess,
      onClose,
      setUserChannels,
      t,
      userChannels,
      userId,
    ]);

    const title = ctxT('user-channels', { screenName });

    const body = useMemo(() => {
      if (!userChannels) {
        return <Fragment />;
      }

      return (
        <div className={classes['channels']}>
          <div className={classes['channels__title']}>{title}</div>

          {isOrgOwnerOrAdmin && (
            <IconLabel
              iconId={'info'}
              iconSize={16}
              color={'#6D767C'}
              label={ctxT('user-channels-org-owner-admin')}
              labelSize={IconLabelSizes.small}
              singleColor
              nonClickable
            />
          )}

          <div className={classes['channels__header']}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={t('common.type-to-search')}
              className={classes['channels__header-search']}
            />
            {showNotifyUsers && (
              <Check
                checked={notifyExistingUsers}
                onChange={setNotifyExistingUsers}
                label={ctxT('notify-new-channel-user')}
                className={classes['channels__header-check']}
              />
            )}
          </div>

          {channelsToDisplay.map(({ id: channelId, name, subdomain, isArchived }, index) => {
            const role = accessedChannels.find(({ id }) => id === channelId)?.role;

            return (
              <div key={channelId} className={classes['channels__item']}>
                <div
                  className={classes['channels__item-name']}
                  onClick={() => channelClickHandler({ subdomain, isArchived })}
                >
                  {name}
                </div>

                {renderUserChannelRole({ channelId, role, index })}

                {Boolean(role) && (
                  <IconLabel
                    iconId={'delete'}
                    iconSize={20}
                    ariaLabel={t('common.remove')}
                    color={getCssVar('--color-danger')}
                    hoverColor={getCssVar('--color-danger-hover')}
                    onClick={() => setChannelIdToRemoveFrom(channelId)}
                    disabled={isOrgOwnerOrAdmin && lastChannelAccess}
                  />
                )}
              </div>
            );
          })}

          {!Boolean(channelsToDisplay.length) && (
            <div className={classes['channels__no-content']}>{ctxT('user-channels-empty')}</div>
          )}
        </div>
      );
    }, [
      accessedChannels,
      channelClickHandler,
      channelsToDisplay,
      ctxT,
      isOrgOwnerOrAdmin,
      lastChannelAccess,
      notifyExistingUsers,
      query,
      renderUserChannelRole,
      showNotifyUsers,
      t,
      title,
      userChannels,
    ]);

    if (!userChannels) {
      return null;
    }

    return (
      <>
        <Modal
          isOpen={Boolean(userChannels)}
          body={body}
          onClose={onClose}
          ariaLabel={title}
          alignTop
        />
        <ConfirmationModal
          isOpen={Boolean(channelIdToRemoveFrom)}
          onClose={() => setChannelIdToRemoveFrom(null)}
          acceptLabel={t('common.delete')}
          onAccept={removeFromChannelHandler}
          title={ctxT(`delete-from${lastChannelAccess ? '-last-' : '-'}channel-confirmation-title`)}
          subTitle={ctxT(
            `delete-from-channel${lastChannelAccess ? '-last-' : '-'}confirmation-sub-title`
          )}
          contentStyle={{ maxWidth: '30rem' }}
          loading={deleteLoading}
          danger
        />
      </>
    );
  }
);
